/**
 * urls.ts
 * ----------------
 * Utility functions related to URLs and related things.
 */

import { LiveSiteEventSeverity } from '../models/status';
import { TimeRangeValue } from '../models/ui/status';

export interface Search {
    [key: string]: SearchValue;
}

type SearchValue = string | string[];

/**
 * Private functions
 */

function isStringArray (search: SearchValue): search is string[] {
    return (search as string[]).map !== undefined;
}

/**
 * Get a dictionary of name-value search pairs for the history view.
 * @param geographies List of filtered geographies
 * @param services List of filtered services
 * @param severities List of filtered severities
 * @param time Filtered time value
 * @returns Search object representation of given parameters
 */
export function buildHistorySearch (
    geographies: string[],
    services: string[],
    severities: LiveSiteEventSeverity[],
    time: TimeRangeValue
): Search {
    return {
        t: time,
        ...geographies.length > 0 ? { geo: geographies } : {},
        ...services.length > 0 ? { svc: services } : {},
        ...severities.length > 0 ? { sev: severities } : {},
    };
}

/**
 * Get the search string value for the given object.
 * @param search Object of search string parameters
 * @returns Search string
 */
export function getSearchString (search: Search) {
    const queryParams = [];

    for (const key in search) {
        if (key === undefined || search[key] === undefined) {
            continue;
        }

        const rawValue = search[key];
        const value = isStringArray(rawValue) ? rawValue.join(',') : rawValue;
        queryParams.push(`${key}=${value}`);
    }

    return `?${queryParams.join('&')}`;
}

/**
 * Parse the given search string into a dictionary of names and values. Valid search string format is assumed.
 * @param search Search string
 * @returns Dictionary of name-value pairs in search string
 */
export function parseSearchString (search: string) {
    const result: { [key: string]: string } = {};

    // Note: substring trims off leading ?
    for (const searchStringPart of search.substring(1).split('&')) {
        const [name, value] = searchStringPart.split('=');
        result[name] = value;
    }

    return result;
}
